import { Button, Card, CardContent, DetailPage } from '@skylabs-digital/backoffice-kit';
import { Check, Copy, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

interface RevealLocationState {
  secret?: string;
}

/**
 * One-shot API-key reveal page.
 *
 * The secret arrives via `location.state` (never the URL). When the user leaves
 * the page — back button, navigation, refresh — the state is gone, so we
 * redirect to the list. This is the routing equivalent of "show once".
 */
export function ApiKeyRevealPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as RevealLocationState | null;
  const [copied, setCopied] = useState(false);

  // Drop the state from history so back-navigation can't show it again.
  // Runs unconditionally to satisfy hooks rules; no-op when there's no state.
  useEffect(() => {
    if (state?.secret) {
      window.history.replaceState({}, document.title);
    }
  }, [state?.secret]);

  if (!state?.secret) {
    return <Navigate to="/api-keys" replace />;
  }
  const { secret } = state;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <DetailPage
      title="API key created"
      description="Copy and store this secret now. You won't see it again."
      breadcrumbs={[{ label: 'API Keys', to: '/api-keys' }, { label: 'New key' }]}
    >
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-warning" />
          <div className="text-sm text-foreground">
            <p className="font-semibold">Shown once</p>
            <p className="text-muted-foreground">
              For security, this key cannot be retrieved later. Store it in your secrets manager
              before leaving this page.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <code className="block flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-sm text-foreground">
              {secret}
            </code>
            <Button onClick={handleCopy} variant={copied ? 'secondary' : 'default'}>
              {copied ? <Check /> : <Copy />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => navigate('/api-keys', { replace: true })}>
          I&apos;ve saved it · Back to API keys
        </Button>
      </div>
    </DetailPage>
  );
}
